import React, { useEffect, useState } from "react";
import { listFlashcards, updateFlashcardStatus } from "../data/flashcardsRepo";
import { useAuth } from "../context/AuthContext";
import FlashcardReview from "../components/flashcards/FlashcardReview";
import useFlashcardStats from "../hooks/useFlashcardStats";
import { RiProgress1Line, RiProgress5Line, RiProgress8Line } from "react-icons/ri";
import { FaPlay, FaArrowRight } from "react-icons/fa";
import BrandBanner from "../components/layout/BrandBanner";
import FlashcardProgress from "../components/dashboard/FlashcardProgress";
import { scheduleNext } from "../utils/flashcardalgorithm";
import FlashcardFinishModal from "../components/flashcards/FlashcardFinishModal";
import { useLocation, useNavigate } from "react-router-dom";

import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";

import "./FlashcardsPage.css";

export default function FlashcardsPage() {
  const { user } = useAuth();
  const [flashcards, setFlashcards] = useState([]);
  const [filter, setFilter] = useState("all");
  const { stats } = useFlashcardStats();

  const [sessionFinished, setSessionFinished] = useState(false);
  const [sessionCards, setSessionCards] = useState([]);
  const [isFilteredSession, setIsFilteredSession] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const inReviewRoute = location.pathname === "/flashcards/review";
  const language = "de"; // TEMP

  // Load flashcards
  useEffect(() => {
    if (!user?.uid) return;

    const fetchFlashcards = async () => {
      try {
        const data = await listFlashcards({
          uid: user.uid,
          language,
          status: filter,
        });
        setFlashcards(data);
      } catch (err) {
        console.error("Error loading flashcards:", err);
      }
    };

    fetchFlashcards();
  }, [user?.uid, filter, language]);

  // If user swipes back (route becomes /flashcards), reset session UI
  useEffect(() => {
    if (!inReviewRoute) {
      setSessionFinished(false);
      setIsFilteredSession(false);
      // Optional: keep sessionCards or clear them
      // setSessionCards([]);
    }
  }, [inReviewRoute]);

  // Update card status
  const handleStatusChange = async (card, newStatus, difficulty = "medium") => {
    const { nextReview } = scheduleNext(card, difficulty);

    try {
      await updateFlashcardStatus({
        uid: user.uid,
        language,
        cardId: card.id,
        status: newStatus,
        difficulty,
        lastReviewed: new Date(),
        timesReviewed: (card.timesReviewed || 0) + 1,
        nextReview,
      });

      setFlashcards((prev) =>
        prev.map((c) =>
          c.id === card.id
            ? {
                ...c,
                status: newStatus,
                difficulty,
                nextReview,
                lastReviewed: new Date(),
                timesReviewed: (card.timesReviewed || 0) + 1,
              }
            : c
        )
      );
    } catch (err) {
      console.error("Error updating flashcard:", err);
    }
  };

  function getSessionCards(cards) {
    const now = new Date();

    const due = cards.filter((c) => {
      if (c.status === "new") return true;

      if (c.nextReview) {
        const next = c.nextReview.toDate ? c.nextReview.toDate() : c.nextReview;
        return next <= now;
      }
      return false;
    });

    return due.slice(0, 30);
  }

  function getCardsByStatus(status) {
    return flashcards.filter((c) => c.status === status);
  }

  const dueCount = getSessionCards(flashcards).length;

  const startSession = () => {
    const cards = getSessionCards(flashcards);
    setSessionCards(cards);
    setIsFilteredSession(false);
    setSessionFinished(false);

    if (analytics) {
      logEvent(analytics, "flashcards_practice_started", {
        type: "all"
      });
    }

    // Push history entry -> swipe-back works
    navigate("/flashcards/review");
  };

  const startFilteredSession = (status) => {
    const cards = getCardsByStatus(status);
    setSessionCards(cards);
    setIsFilteredSession(true);
    setSessionFinished(false);

    if (analytics) {
      logEvent(analytics, "flashcards_practice_started", {
        type: status
      });
    }

    navigate("/flashcards/review");
  };

  const exitPractice = () => {
    // Going back to main screen
    navigate("/flashcards", { replace: true });
    window.scrollTo(0, 0);
  };

  return (
    <>
      {/* Show BrandBanner only on main flashcards screen */}
      {!inReviewRoute && !sessionFinished && <BrandBanner />}

      <div className={`flashcards-page ${inReviewRoute ? "review-mode" : ""}`}>
        {sessionFinished ? (
          <FlashcardFinishModal
            reviewedCount={sessionCards.length}
            onClose={() => {
              setSessionFinished(false);
              navigate("/flashcards", { replace: true });
            }}
          />
        ) : inReviewRoute ? (
          <FlashcardReview
            flashcards={sessionCards}
            onStatusChange={handleStatusChange}
            onSessionEnd={() => setSessionFinished(true)}
            isFilteredSession={isFilteredSession}
            onExitPractice={exitPractice}
          />
        ) : (
          <>
            <h1 className="flashcards-title">Meus Flashcards</h1>

            {flashcards.length === 0 && (
              <p className="flashcards-empty-info">
                Você ainda não salvou nenhum flashcard. Enquanto lê uma história,
                toque em uma palavra e salve para criar seus flashcards.
              </p>
            )}

            <div className="flashcard-page-progress-section">
              <FlashcardProgress compact />
            </div>

            <button
              className={`practice-btn ${dueCount === 0 ? "empty" : ""}`}
              onClick={() => dueCount > 0 && startSession()}
            >
              {dueCount > 0 ? (
                <div className="practice-btn-text">
                  <span className="practice-btn-title">Bora praticar</span>
                  <FaPlay className="practice-btn-icon" />
                  <span className="practice-btn-subtext">
                    {dueCount} flashcard{dueCount > 1 ? "s" : ""} para revisar
                  </span>
                </div>
              ) : (
                <div className="practice-btn-text">
                  <span className="practice-btn-title">🎉 Nada para revisar agora</span>
                </div>
              )}
            </button>

            <div className="flashcard-status-buttons">
              <button
                className="status-btn status-new"
                onClick={() => startFilteredSession("new")}
              >
                <div className="status-left">
                  <RiProgress1Line size={20} />
                  <span>Novo</span>
                </div>
                <div className="status-right">
                  <span className="status-count">{stats.new}</span>
                  <FaArrowRight size={14} />
                </div>
              </button>

              <button
                className="status-btn status-learning"
                onClick={() => startFilteredSession("learning")}
              >
                <div className="status-left">
                  <RiProgress5Line size={20} />
                  <span>Treino</span>
                </div>
                <div className="status-right">
                  <span className="status-count">{stats.learning}</span>
                  <FaArrowRight size={14} />
                </div>
              </button>

              <button
                className="status-btn status-known"
                onClick={() => startFilteredSession("known")}
              >
                <div className="status-left">
                  <RiProgress8Line size={20} />
                  <span>Já Sei</span>
                </div>
                <div className="status-right">
                  <span className="status-count">{stats.known}</span>
                  <FaArrowRight size={14} />
                </div>
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
